import { orgAPI } from "@/api/api";
import { useQuery } from "@tanstack/react-query";

interface UseCalendarEventFetchProps {
  integrationConnectionId?: string;
  eventId: string;
  calendarId?: string;
  enabled?: boolean;
}

/** The fetched CalendarEvent data, as a loose record. */
export type CalendarEventDetail = Record<string, unknown>;

// google-calendar-event-get returns a single entity ({ base, type, platform,
// data }); be tolerant of an array wrapper too.
function parseEvent(raw: unknown): CalendarEventDetail | null {
  if (!raw || typeof raw !== "object") return null;
  if (Array.isArray(raw)) {
    const first = raw.find(
      (item) => item && typeof item === "object" && "data" in item,
    ) as { data?: CalendarEventDetail } | undefined;
    return first?.data ?? null;
  }
  if ("data" in raw) {
    return (
      ((raw as { data?: CalendarEventDetail }).data as
        | CalendarEventDetail
        | undefined) ?? null
    );
  }
  return null;
}

export function useCalendarEventFetch({
  integrationConnectionId,
  eventId,
  calendarId = "primary",
  enabled = true,
}: UseCalendarEventFetchProps) {
  return useQuery({
    queryKey: [
      "google-calendar-event",
      integrationConnectionId,
      calendarId,
      eventId,
    ],
    queryFn: async () => {
      if (!integrationConnectionId) {
        throw new Error("Missing integration connection ID");
      }
      const response = await orgAPI.toolbox.execute({
        integration_connection_id: integrationConnectionId,
        action_name: "google-calendar-event-get",
        action_input: { calendar_id: calendarId, event_id: eventId },
      });
      return parseEvent(response.data);
    },
    enabled: !!integrationConnectionId && !!eventId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
